// Export dialog (F-13, D-53): one comparison video for sharing — layout, range, labels, opening, options and
// where to save it. The screenshot button replaces the still export.

import { ALL_FORMATS, BlobSource, Input } from 'mediabunny';
import type { App } from '../app/app';
import { writableDir } from '../app/app';
import { ExportJob, planExport, type ExportOptions } from '../export/exporter';
import type { ClientTemplate } from '../export/templates';
import { fsAccess, kvGet, kvSet } from '../app/persist';
import { t } from '../i18n';
import { $, downloadBlob, esc, message, openModal, toast } from './dom';
import { pendingAlignments } from './badges';
import { slotLabel } from '../core/types';

const TEMPLATES: ClientTemplate[] = ['sbs', 'sbs-zoom', 'wipe', 'quad'];
type Opening = 'none' | 'card' | 'video';

interface State {
  template: ClientTemplate;
  range: 'all' | 'io';
  before: string;
  after: string;
  opening: Opening;
  slateTitle: string;
  slateSubtitle: string;
  intro: File | null;
  introSec: number;
  audio: boolean;
  half: boolean;
  playAfter: boolean;
  codec: 'avc' | 'hevc';
  bitrate: number;
  maxRate: boolean;
  folder: FileSystemDirectoryHandle | null;
  fileName: string;
  /** The file name was typed by the user: a template change no longer renames it. */
  nameEdited: boolean;
}

const s: State = {
  template: 'sbs',
  range: 'all',
  before: 'Before',
  after: 'After',
  opening: 'none',
  slateTitle: '',
  slateSubtitle: '',
  intro: null,
  introSec: 0,
  audio: true,
  half: false,
  playAfter: true,
  codec: 'avc',
  bitrate: 0,
  maxRate: false,
  folder: null,
  fileName: '',
  nameEdited: false,
};

let job: ExportJob | null = null;

/** Default bitrate scales with pixel count (P-12): ~80 Mbps at 3840×2160 for H.264. */
function defaultBitrate(w: number, h: number, codec: 'avc' | 'hevc'): number {
  const base = (80 * (w * h)) / (3840 * 2160);
  return Math.max(12, Math.round(codec === 'hevc' ? base * 0.6 : base));
}

export function options(app: App): ExportOptions {
  const o: ExportOptions = {
    kind: 'client',
    template: s.template,
    range: s.range,
    maxRate: s.maxRate,
    half: s.half,
    codec: s.codec,
    bitrateMbps: 0,
    audio: s.audio,
    internal: { burnTc: false, burnAlign: false, applyAdjust: false, burnCross: false, burnGrid: false },
    client: {
      before: s.before,
      after: s.after,
      slate: s.opening === 'card',
      slateTitle: s.slateTitle,
      slateSubtitle: s.slateSubtitle,
      intro: s.opening === 'video' ? s.intro : null,
      introSec: s.introSec,
    },
  };
  const plan = planExport(app, o);
  o.bitrateMbps = s.bitrate || defaultBitrate(plan.layout.width, plan.layout.height, s.codec);
  return o;
}

function baseName(app: App): string {
  return (app.project.name || 'aicomp').replace(/[\\/:*?"<>|]+/g, '_');
}

/** "<project>_<template>.mp4", e.g. shot010_sbs-zoom.mp4 (owner's choice). */
function defaultFileName(app: App): string {
  return `${baseName(app)}_${s.template}.mp4`;
}

export function initExport(app: App): void {
  $('#btnExport').addEventListener('click', () => void open(app));
  const body = $('#exportModal');
  body.addEventListener('click', (e) => {
    const el = e.target as HTMLElement;
    const seg = el.closest<HTMLElement>('[data-seg] button[data-v]');
    if (seg) {
      const key = seg.parentElement!.dataset.seg as 'template' | 'range' | 'opening';
      (s as unknown as Record<string, string>)[key] = seg.dataset.v!;
      if (key === 'template' && !s.nameEdited) s.fileName = defaultFileName(app);
      build(app);
      return;
    }
    const act = el.closest<HTMLElement>('[data-act]')?.dataset.act;
    if (act === 'intro') void pickIntro(app);
    else if (act === 'folder') void pickFolder(app);
  });
  const onChange = (e: Event) => {
    const el = e.target as HTMLInputElement;
    const key = el.dataset.opt as keyof State | undefined;
    if (!key) return;
    if (el.type === 'checkbox') (s as unknown as Record<string, unknown>)[key] = el.checked;
    else if (el.type === 'number') (s as unknown as Record<string, unknown>)[key] = Number(el.value);
    else (s as unknown as Record<string, unknown>)[key] = el.value;
    if (key === 'fileName') s.nameEdited = true;
    if (key === 'codec') s.bitrate = 0;
    summary(app);
  };
  body.addEventListener('change', onChange);
  body.addEventListener('input', onChange);
  $('#exportGo').addEventListener('click', () => void go(app));
  $('#exportCancel').addEventListener('click', () => job?.cancel());
  $('#playerClose').addEventListener('click', () => closePlayer());
  $('#playerModal').addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closePlayer();
  });
}

export function updateExport(_app: App): void {}

async function open(app: App): Promise<void> {
  // Save to the project's folder (F-18.10), else where the last export went.
  s.folder = app.ws.activeProject()?.mediaDir ?? (await kvGet<FileSystemDirectoryHandle>('exportFolder')) ?? null;
  if (!s.nameEdited || !s.fileName) {
    s.fileName = defaultFileName(app);
    s.nameEdited = false;
  }
  build(app);
  openModal('#exportModal');
}

function seg(key: 'template' | 'range' | 'opening', items: [string, string][], cur: string): string {
  return `<div class="seg" data-seg="${key}">${items.map(([v, l]) => `<button data-v="${v}" class="${v === cur ? 'on' : ''}">${esc(l)}</button>`).join('')}</div>`;
}

function check(key: keyof State, label: string): string {
  return `<label class="check"><input type="checkbox" data-opt="${key}" ${s[key] ? 'checked' : ''}><span>${esc(t(label))}</span></label>`;
}

function row(label: string, content: string, cls = ''): string {
  return `<div class="xrow ${cls}"><span class="xl">${label ? esc(t(label)) : ''}</span><div class="xc">${content}</div></div>`;
}

function build(app: App): void {
  const text = (key: keyof State, ph: string) => `<input type="text" data-opt="${key}" value="${esc(String(s[key]))}" placeholder="${esc(t(ph))}">`;
  let html =
    row('export.template', seg('template', TEMPLATES.map((v) => [v, t('export.tpl.' + v)]), s.template)) +
    row('export.range', seg('range', [['all', t('export.rangeAll')], ['io', t('export.rangeIO')]], s.range)) +
    row('export.labels', text('before', 'export.labelBefore') + text('after', 'export.labelAfter')) +
    row('export.opening', seg('opening', [['none', t('export.openingNone')], ['card', t('export.openingCard')], ['video', t('export.openingVideo')]], s.opening));
  if (s.opening === 'card') html += row('', text('slateTitle', 'export.slateTitle') + text('slateSubtitle', 'export.slateSubtitle'), 'sub');
  if (s.opening === 'video')
    html += row(
      '',
      `<button data-act="intro">${esc(t('export.introPick'))}</button><span class="muted small ellipsis">${esc(s.intro ? t('export.introFile', { name: s.intro.name, s: Math.round(s.introSec * 10) / 10 }) : t('export.introNone'))}</span>`,
      'sub',
    );
  html += row('export.options', check('audio', 'export.audio') + check('half', 'export.half') + check('playAfter', 'export.playAfter'));
  html += row(
    'export.codec',
    `<select data-opt="codec"><option value="avc" ${s.codec === 'avc' ? 'selected' : ''}>H.264</option><option value="hevc" ${s.codec === 'hevc' ? 'selected' : ''}>HEVC</option></select>` +
      `<input type="number" class="num" min="4" max="400" step="1" data-opt="bitrate" value="${s.bitrate || ''}" placeholder="${esc(t('export.bitrateAuto'))}"><span class="muted small">Mbps</span>` +
      (app.sources.timeline.ticksPerFrame > 1 ? check('maxRate', 'export.maxRate') : ''),
  );
  $('#exportForm').innerHTML = html;
  // Destination: the folder button and the file name (the Export button sits at the right of this row).
  const folder = $('#exportFolder');
  folder.textContent = s.folder ? `📁 ${s.folder.name}` : t('export.folderPick');
  folder.title = t('export.folderTip');
  folder.hidden = !fsAccess.supported;
  const name = $<HTMLInputElement>('#exportName');
  if (document.activeElement !== name) name.value = s.fileName;
  summary(app);
}

function summary(app: App): void {
  const lines: string[] = [];
  if (!app.sources.has('A')) lines.push(`<span class="warn">${esc(t('export.needA'))}</span>`);
  // Alignment reminder (F-03.10): comparing a version that is still offset shows the offset, not the AI's changes.
  const off = pendingAlignments(app);
  if (off.length) lines.push(`<span class="warn">${esc(t('export.alignWarn', { ids: off.map(slotLabel).join('、') }))}</span>`);
  if (app.sources.has('A')) {
    const o = options(app);
    const plan = planExport(app, o);
    const aud = app.project.audioSourceId;
    lines.push(
      esc(
        [
          t('export.sumSize', { w: plan.layout.width, h: plan.layout.height }),
          t('export.sumDur', { s: Math.round(plan.durationSec * 10) / 10 }),
          t('export.sumMb', { mb: Math.max(1, Math.round(plan.estimatedBytes / 1e6)) }),
          s.audio && aud ? t('export.sumAudioOn', { a: slotLabel(aud) }) : t('export.sumAudioOff'),
        ].join(' · '),
      ),
    );
    if (plan.scaledNote) lines.push(`<span class="warn">${esc(t('export.sumScaled', { p: Math.round(plan.layout.scale * 100) }))}</span>`);
  }
  $('#exportSummary').innerHTML = lines.map((l) => `<div>${l}</div>`).join('');
  $<HTMLButtonElement>('#exportGo').disabled = !app.sources.has('A') || !!job;
}

async function pickIntro(app: App): Promise<void> {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'video/*,.mp4,.mov,.m4v,.mkv,.webm';
  input.onchange = async () => {
    const f = input.files?.[0];
    if (!f) return;
    const inp = new Input({ formats: ALL_FORMATS, source: new BlobSource(f) });
    try {
      const tr = await inp.getPrimaryVideoTrack();
      if (!tr) throw new Error('no video');
      s.introSec = await tr.computeDuration();
      s.intro = f;
    } catch {
      toast(t('export.errIntro', { name: f.name }), 5000);
    } finally {
      inp.dispose();
    }
    build(app);
  };
  input.click();
}

async function pickFolder(app: App): Promise<FileSystemDirectoryHandle | null> {
  try {
    const dir = await fsAccess.pickDirectory('readwrite');
    if (!dir) return null;
    s.folder = dir;
    await kvSet('exportFolder', dir);
    build(app);
    return dir;
  } catch (e) {
    if ((e as DOMException).name !== 'AbortError') toast(t('toast.folderFailed', { detail: String(e) }), 5000);
    return null;
  }
}

/** The file to write in the chosen folder; asks before replacing an existing file. Null = cancelled. */
async function targetFile(app: App, name: string): Promise<FileSystemFileHandle | null> {
  let dir = s.folder && (await writableDir(s.folder, true)) ? s.folder : null;
  dir ??= await pickFolder(app);
  if (!dir) return null;
  const exists = await dir.getFileHandle(name).then(
    () => true,
    () => false,
  );
  if (exists) {
    const c = await message(t('export.replaceTitle'), `<p>${esc(t('export.replaceBody', { name, folder: dir.name }))}</p>`, [
      { label: t('export.replace'), primary: true },
      { label: t('common.cancel') },
    ]);
    if (c !== 0) return null;
  }
  return dir.getFileHandle(name, { create: true });
}

async function go(app: App): Promise<void> {
  if (!app.sources.has('A') || job) return;
  if (s.opening === 'video' && !s.intro) {
    toast(t('export.introNone'), 4000);
    return;
  }
  if (app.player.playing) app.player.pause();
  let name = ($<HTMLInputElement>('#exportName').value.trim() || defaultFileName(app)).replace(/[\\/:*?"<>|]+/g, '_');
  if (!/\.mp4$/i.test(name)) name += '.mp4';
  s.fileName = name;
  const o = options(app);
  let writable: FileSystemWritableFileStream | null = null;
  let handle: FileSystemFileHandle | null = null;
  try {
    if (fsAccess.supported) {
      handle = await targetFile(app, name);
      if (!handle) return;
      writable = await handle.createWritable();
    }
  } catch (e) {
    if ((e as DOMException).name === 'AbortError') return;
    writable = null;
  }
  job = new ExportJob(app, o);
  job.onProgress = (p, n, total) => setProgress(p, `${n} / ${total}`);
  showProgress(true);
  const t0 = performance.now();
  try {
    const blob = await job.run(writable);
    if (job.cancelled) {
      toast(t('export.cancelled'));
      return;
    }
    if (blob) downloadBlob(blob, name);
    toast(t('export.done', { s: Math.round((performance.now() - t0) / 100) / 10 }), 5000);
    if (s.playAfter) {
      // Play what was written (read back from the file), so the result is checked as it will be delivered.
      const file = blob ?? (handle ? await handle.getFile().catch(() => null) : null);
      if (file) playExported(file, handle?.name ?? name);
    }
  } catch (e) {
    toast(t('export.failed', { detail: String(e) }), 8000);
  } finally {
    job = null;
    showProgress(false);
  }
}

let playerUrl = '';

/** Plays an exported video in the player dialog (F-13.7). */
export function playExported(file: Blob, name: string): void {
  const v = $<HTMLVideoElement>('#playerVideo');
  if (playerUrl) URL.revokeObjectURL(playerUrl);
  playerUrl = URL.createObjectURL(file);
  $('#playerTitle').textContent = t('player.title', { name });
  v.src = playerUrl;
  $('#exportModal').hidden = true;
  openModal('#playerModal');
  void v.play().catch(() => undefined);
}

export function closePlayer(): void {
  const v = $<HTMLVideoElement>('#playerVideo');
  v.pause();
  v.removeAttribute('src');
  v.load();
  if (playerUrl) URL.revokeObjectURL(playerUrl);
  playerUrl = '';
  $('#playerModal').hidden = true;
}

function showProgress(on: boolean): void {
  $('#exportProgress').hidden = !on;
  $<HTMLButtonElement>('#exportGo').disabled = on;
  if (on) setProgress(0, '');
}

function setProgress(p: number, text: string): void {
  $<HTMLProgressElement>('#exportBar').value = p;
  $('#exportProgText').textContent = `${Math.round(p * 100)}% ${text}`;
}
